import {
  CheckCircleOutlined,
  HelpOutline,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { t } from "i18next";
import {
  IUrlEmbedCheck,
  UrlEmbedBlocker,
  UrlEmbedStatus,
} from "../../../../../types/NendaTypes";

/**
 * Permanent indicator of whether a URL asset can actually be rendered on a
 * screen.
 *
 * Deliberately shown wherever an asset can be picked, not just while creating
 * it: an un-embeddable page otherwise fails silently on the screen, long after
 * anyone would connect the two.
 */
/**
 * Maps the machine-readable blocker to a sentence a user can act on.
 *
 * `embedCheck.detail` is deliberately NOT rendered: it is server-generated
 * English diagnostic text ("X-Frame-Options: DENY", "No headless browser is
 * configured") that both leaks implementation detail into the portal and can
 * never be translated. It stays on the asset for support and logs only.
 */
const reasonKey = (blockedBy?: UrlEmbedBlocker): string | undefined => {
  switch (blockedBy) {
    case UrlEmbedBlocker.X_FRAME_OPTIONS:
    case UrlEmbedBlocker.CONTENT_SECURITY_POLICY:
      return "customerportal.pages.assets.embed_check.reason.embedding_not_allowed";
    case UrlEmbedBlocker.HTTP_STATUS:
      return "customerportal.pages.assets.embed_check.reason.error_response";
    case UrlEmbedBlocker.UNREACHABLE:
      return "customerportal.pages.assets.embed_check.reason.unreachable";
    default:
      return undefined;
  }
};

const UrlEmbedBadge = ({
  embedCheck,
  showWhenEmbeddable = false,
  compact = false,
}: {
  embedCheck?: IUrlEmbedCheck;
  /** Grids stay quieter when a healthy asset shows no badge at all. */
  showWhenEmbeddable?: boolean;
  compact?: boolean;
}) => {
  const theme = useTheme();

  if (!embedCheck) return null;
  if (embedCheck.status === UrlEmbedStatus.EMBEDDABLE && !showWhenEmbeddable) {
    return null;
  }

  const checkedAt = embedCheck.checkedAt
    ? new Date(embedCheck.checkedAt).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : undefined;

  const config = {
    [UrlEmbedStatus.BLOCKED]: {
      Icon: WarningAmberOutlined,
      color: theme.palette.warning.main,
      label: t("customerportal.pages.assets.embed_check.blocked_label"),
      description: t("customerportal.pages.assets.embed_check.blocked_tooltip"),
    },
    [UrlEmbedStatus.UNVERIFIED]: {
      Icon: HelpOutline,
      color: theme.palette.grey[600],
      label: t("customerportal.pages.assets.embed_check.unverified_label"),
      description: t(
        "customerportal.pages.assets.embed_check.unverified_tooltip"
      ),
    },
    [UrlEmbedStatus.EMBEDDABLE]: {
      Icon: CheckCircleOutlined,
      color: theme.palette.success.main,
      label: t("customerportal.pages.assets.embed_check.embeddable_label"),
      description: t(
        "customerportal.pages.assets.embed_check.embeddable_tooltip"
      ),
    },
  }[embedCheck.status];

  if (!config) return null;

  const { Icon, color, label, description } = config;

  const reason = reasonKey(embedCheck.blockedBy);

  const tooltip = [
    reason && t(reason),
    description,
    checkedAt &&
      t("customerportal.pages.assets.embed_check.checked_at", {
        date: checkedAt,
      }),
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.25rem",
          color,
        }}
      >
        <Icon sx={{ fontSize: compact ? "0.9rem" : "1.1rem" }} />
        {!compact && (
          <Typography
            variant="caption"
            /* text.secondary is near-white and unreadable on these light
               surfaces; the status colour carries the meaning instead. */
            sx={{ color, lineHeight: 1 }}
          >
            {label}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

export default UrlEmbedBadge;
